import { ArrowLeft } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import {
  createInbox,
  deleteInbox,
  getFacebookLoginConfig,
  getInstagramLoginConfig,
  getWhatsappSignupConfig,
  instagramConnect,
  listInboxes,
} from '../../api/endpoints';
import type {
  FacebookLoginConfig,
  InstagramLoginConfig,
  SmsDefaultPrompt as SmsDefaultQuestion,
  WhatsappSignupConfig,
} from '../../api/types';
import { Field } from '../../components/Field';
import { ErrorNote, Note } from '../../components/ErrorNote';
import { Spinner } from '../../components/Spinner';
import {
  emptyForm,
  toCreateBody,
  validateForm,
  type ChannelForm,
  type FieldErrors,
} from '../../lib/channelForms';
import { channelHealth, connectOption, identityLine } from '../../lib/channelMeta';
import { launchConnectOauth } from '../../oauth/launch';
import { useLoad } from '../../lib/useLoad';
import { clearInstagramGrant, peekInstagramGrant } from '../../oauth/oauthReturn';
import { useOauthReturn, type OauthOutcome } from '../../oauth/useOauthReturn';
import { ChannelFields } from './fields';
import { FacebookPages } from './FacebookPages';
import { InstagramLogin } from './InstagramLogin';
import { SmsDefaultPrompt } from './SmsDefaultPrompt';
import { WhatsappEmbedded } from './WhatsappEmbedded';
import './connect.css';

type Phase =
  | { kind: 'form' }
  | { kind: 'probing' }
  | { kind: 'leaving' }
  /** An Instagram sign-in came back with a grant; the inbox is being created from it. */
  | { kind: 'redeeming' }
  | {
      kind: 'done';
      identity: string;
      detail?: string | null;
      /** What the number means for automated SMS — set only by POST /inboxes. */
      inboxId?: number;
      smsDefault?: SmsDefaultQuestion | null;
    };

/** What the demoted credential form is called, per channel, when a sign-in is the path. */
const MANUAL_SUMMARY: Partial<Record<string, string>> = {
  whatsapp: 'Connect manually with a system-user token instead',
  facebook: 'Connect manually with a Page access token instead',
  instagram: 'Connect manually with an access token instead',
};

/** The consent came back to this URL — verify or explain, offer retry/cleanup. */
function ReturnPanel({ outcome, slug }: { outcome: OauthOutcome; slug: string }) {
  const navigate = useNavigate();
  const inboxes = useLoad(listInboxes);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const inboxId = outcome.marker?.inboxId ?? null;

  const retry = async () => {
    if (inboxId === null) return;
    setBusy(true);
    setError(null);
    try {
      await launchConnectOauth(inboxId, slug as 'google' | 'microsoft' | 'ringcentral');
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : 'Could not restart the sign-in.');
    }
  };

  const remove = async () => {
    if (inboxId === null) return;
    setBusy(true);
    setError(null);
    try {
      await deleteInbox(inboxId);
      navigate('/');
    } catch (e) {
      setBusy(false);
      setError(e instanceof Error ? e.message : 'Could not remove the inbox.');
    }
  };

  if (outcome.kind === 'returned') {
    if (inboxes.loading) {
      return (
        <div className="card card-pad connect-panel">
          <Spinner large />
          <p>Confirming the connection…</p>
        </div>
      );
    }
    const target = inboxes.data?.find((inbox) => inbox.id === inboxId);
    if (target && channelHealth(target) === 'connected') {
      return (
        <div className="card card-pad connect-panel">
          <Note tone="ok">
            <strong>{identityLine(target)}</strong> is connected and ready to use.
          </Note>
          {/* A FIRST sign-in carries the default-sender question; a reconnect never does. */}
          {outcome.smsDefault && (
            <SmsDefaultPrompt inboxId={target.id} identity={identityLine(target)} prompt={outcome.smsDefault} />
          )}
          <Link to="/" className="btn btn-primary">
            Back to channels
          </Link>
        </div>
      );
    }
    // Came back without an error but the inbox doesn't read connected.
  }

  const message =
    outcome.kind === 'error'
      ? outcome.message
      : 'The sign-in didn’t finish — the provider never confirmed access.';

  return (
    <div className="card card-pad connect-panel">
      <Note tone={outcome.kind === 'error' ? 'danger' : 'warn'}>{message}</Note>
      {error && <ErrorNote error={error} />}
      <p className="connect-panel-hint">
        {inboxId === null
          ? 'Nothing was saved — start the sign-in again when you are ready.'
          : 'The inbox was already created; retry the sign-in to finish connecting it, or remove it to start over.'}
      </p>
      <div className="connect-panel-actions">
        {inboxId !== null && (
          <>
            <button className="btn btn-primary" onClick={() => void retry()} disabled={busy}>
              {busy && <Spinner />}
              Try the sign-in again
            </button>
            <button className="btn btn-danger" onClick={() => void remove()} disabled={busy}>
              Remove this inbox
            </button>
          </>
        )}
        <Link to="/" className="btn btn-secondary">
          Back to channels
        </Link>
      </div>
    </div>
  );
}

export function ConnectForm() {
  const { slug } = useParams();
  const option = connectOption(slug ?? '');
  const outcome = useOauthReturn();

  const [form, setForm] = useState<ChannelForm>(() => emptyForm((option?.slug ?? 'telegram')));
  const [errors, setErrors] = useState<FieldErrors>({});
  // An Instagram sign-in coming back: the callback relayed a grant, and this is
  // the authenticated half that turns it into an inbox. Read once (StrictMode
  // renders twice), and the screen opens straight on the redeeming panel.
  const [grant] = useState(() => (slug === 'instagram' ? peekInstagramGrant() : null));
  const [phase, setPhase] = useState<Phase>(() => (grant ? { kind: 'redeeming' } : { kind: 'form' }));
  const [serverError, setServerError] = useState<string | null>(null);

  // The three Meta channels have a sign-in onboarding; every other slug skips
  // the request. A failed load degrades to the manual form rather than
  // blocking the page.
  const signup = useLoad<WhatsappSignupConfig | null>(
    () => (slug === 'whatsapp' ? getWhatsappSignupConfig() : Promise.resolve(null)),
    [slug],
  );
  const facebookLogin = useLoad<FacebookLoginConfig | null>(
    () => (slug === 'facebook' || slug === 'instagram' ? getFacebookLoginConfig() : Promise.resolve(null)),
    [slug],
  );
  const instagramLogin = useLoad<InstagramLoginConfig | null>(
    () => (slug === 'instagram' ? getInstagramLoginConfig() : Promise.resolve(null)),
    [slug],
  );

  // Redeemed exactly once: the code inside the grant is single-use at Meta, and
  // StrictMode runs effects twice in development — a second post would fail at
  // Meta and paint an error over an inbox that was just created.
  const redeemed = useRef(false);
  useEffect(() => {
    if (!grant || redeemed.current) return;
    redeemed.current = true;
    clearInstagramGrant();
    void instagramConnect({ grant })
      .then((inbox) => setPhase({ kind: 'done', identity: identityLine(inbox) }))
      .catch((e: unknown) => {
        setPhase({ kind: 'form' });
        setServerError(e instanceof Error ? e.message : 'The Instagram account could not be connected.');
      });
  }, [grant]);

  if (!option) return <Navigate to="/connect" replace />;

  const embedded = option.slug === 'whatsapp' && signup.data?.available ? signup.data : null;
  const facebookPages = option.slug === 'facebook' && facebookLogin.data?.available ? facebookLogin.data : null;
  const instagramSignIn = option.slug === 'instagram' && instagramLogin.data?.available;
  const signInPanel = !!(embedded || facebookPages || instagramSignIn);
  const signupResolving =
    (option.slug === 'whatsapp' && signup.loading) ||
    (option.slug === 'facebook' && facebookLogin.loading) ||
    (option.slug === 'instagram' && (instagramLogin.loading || facebookLogin.loading));

  const oauthLeg =
    option.slug === 'google' ||
    option.slug === 'microsoft' ||
    (form.kind === 'ringcentral' && form.auth === 'oauth');

  const submit = async () => {
    const found = validateForm(form);
    setErrors(found);
    if (Object.keys(found).length) return;
    setServerError(null);
    setPhase({ kind: oauthLeg ? 'leaving' : 'probing' });
    try {
      const inbox = await createInbox(toCreateBody(form));
      if (oauthLeg) {
        await launchConnectOauth(inbox.id, option.slug as 'google' | 'microsoft' | 'ringcentral');
        return; // navigation away
      }
      setPhase({
        kind: 'done',
        identity: identityLine(inbox),
        inboxId: inbox.id,
        smsDefault: inbox.smsDefault ?? null,
      });
    } catch (e) {
      setPhase({ kind: 'form' });
      setServerError(e instanceof Error ? e.message : 'The connection could not be created.');
    }
  };

  return (
    <main className="page page-narrow">
      <Link to="/connect" className="back-link">
        <ArrowLeft size={14} />
        Channel types
      </Link>
      <div className="page-head">
        <div>
          <h1 className="page-title">{option.label}</h1>
          <p className="page-sub">{option.description}</p>
        </div>
      </div>

      {outcome ? (
        <ReturnPanel outcome={outcome} slug={option.slug} />
      ) : phase.kind === 'done' ? (
        <div className="card card-pad connect-panel">
          <Note tone="ok">
            <strong>{phase.identity}</strong> is connected and ready to use.
          </Note>
          {phase.detail && <Note tone="info">{phase.detail}</Note>}
          {phase.smsDefault && phase.inboxId !== undefined && (
            <SmsDefaultPrompt inboxId={phase.inboxId} identity={phase.identity} prompt={phase.smsDefault} />
          )}
          <Link to="/" className="btn btn-primary">
            Back to channels
          </Link>
        </div>
      ) : phase.kind === 'redeeming' ? (
        <div className="card card-pad connect-panel">
          <Spinner large />
          <p>
            <strong>Finishing the Instagram connection…</strong>
          </p>
          <p className="connect-panel-hint">
            Exchanging the sign-in for an access token, reading the account and subscribing it to
            message events.
          </p>
        </div>
      ) : phase.kind === 'probing' ? (
        <div className="card card-pad connect-panel">
          <Spinner large />
          <p>
            <strong>Testing the connection…</strong>
          </p>
          <p className="connect-panel-hint">
            We sign in to the provider with these credentials before saving — this can take up to a
            minute.
          </p>
        </div>
      ) : phase.kind === 'leaving' ? (
        <div className="card card-pad connect-panel">
          <Spinner large />
          <p>Sending you to the provider to approve access…</p>
        </div>
      ) : signupResolving ? (
        <div className="card card-pad connect-panel">
          <Spinner large />
        </div>
      ) : (
        <>
          {embedded && (
            <WhatsappEmbedded
              config={embedded}
              onDone={(inbox) => setPhase({ kind: 'done', identity: identityLine(inbox) })}
            />
          )}
          {facebookPages && (
            <FacebookPages
              config={facebookPages}
              onDone={(result) =>
                setPhase({
                  kind: 'done',
                  identity: identityLine(result.inbox),
                  detail: result.instagramInbox
                    ? `${identityLine(result.instagramInbox)} was connected as an Instagram inbox on the same sign-in.`
                    : result.instagramNote,
                })
              }
            />
          )}
          {instagramSignIn && <InstagramLogin facebookAvailable={!!facebookLogin.data?.available} />}
          {/* With a sign-in available the credential form is the fallback, not the path. */}
          <MaybeManualDetails collapsed={signInPanel} summary={MANUAL_SUMMARY[option.slug]}>
            <form
              className="card card-pad connect-form"
              // Nothing in here is a person's login, but it is a text field
              // followed by a token field — which is exactly the shape Chrome
              // saves as one. The individual fields carry their own hints
              // (see fields.tsx); this is the form-level half of the same
              // fight, so a provider's API token never lands in the browser's
              // password manager as a credential for this origin.
              autoComplete="off"
              onSubmit={(e) => {
                e.preventDefault();
                void submit();
              }}
            >
              {serverError && <ErrorNote error={serverError} />}

              <Field
                label="Inbox name"
                error={errors['name']}
                hint="Shown to your team in the dashboard."
              >
                <input
                  className="input"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  aria-invalid={!!errors['name']}
                  // The text field directly above the channel's token: left
                  // unhinted, this is the one Chrome pairs with it and offers
                  // to remember as a username.
                  autoComplete="off"
                  data-1p-ignore
                  data-lpignore="true"
                />
              </Field>

              <ChannelFields form={form} errors={errors} onChange={setForm} />

              {oauthLeg && (
                <Note tone="info">
                  You’ll be sent to {option.slug === 'ringcentral' ? 'RingCentral' : option.label}{' '}
                  to approve access, then brought back here. If you abandon the approval, you’ll
                  land on an error page — return to this site yourself and retry.
                </Note>
              )}

              <div className="connect-form-actions">
                <button className="btn btn-primary" type="submit">
                  {oauthLeg ? 'Create & continue to sign-in' : 'Test & connect'}
                </button>
              </div>
            </form>
          </MaybeManualDetails>
        </>
      )}
    </main>
  );
}

/** Tucks the manual credential form behind a disclosure when a sign-in path exists. */
function MaybeManualDetails({
  collapsed,
  summary,
  children,
}: {
  collapsed: boolean;
  summary?: string;
  children: ReactNode;
}) {
  if (!collapsed) return children;
  return (
    <details className="connect-manual">
      <summary>{summary ?? 'Connect manually with credentials instead'}</summary>
      {children}
    </details>
  );
}
